import type { TeamOption } from '../../services/game/gameOptionsService'
import type { SelectOption } from './venueSelection'

/**
 * Options for the event form's Team select.
 *
 * Pure and platform-agnostic so web and mobile label the same list identically
 * — and so both match the add/edit Game modal's Home/Visiting Team pickers,
 * which read the same `/schedules/:id/teams/simple` response.
 */

/** A team's display name, from either key the API populates. */
export const teamLabel = (team: TeamOption): string =>
  team.name_full?.trim() || team.name?.trim() || ''

/**
 * Team options for a competition event.
 *
 * **Every team the competition has, in the same order the API returns them** —
 * deliberately the SAME list the add/edit Game modal's team pickers show, so
 * the two never disagree about what teams exist.
 *
 * Placeholders ("TBD West", "Winner of Semifinal 1") are INCLUDED. An earlier
 * version filtered them out, on the reasoning that a bracket slot is
 * meaningless for an event — but that is the admin's call, not this function's,
 * and the two lists silently disagreeing reads as a bug in the fetch. Unlike a
 * game, an event needs nothing from the team a placeholder lacks: it already
 * belongs to the schedule, so there is no division for the API to infer.
 *
 * Only unnamed entries are dropped — an option with a blank label is unpickable
 * and unreadable.
 *
 * No crest: the `simple` projection sends no `image` (see `TeamOption`), so
 * carrying one would only ever produce a placeholder shield on every row.
 */
export const buildTeamOptions = (teams: TeamOption[]): SelectOption[] =>
  teams
    .map((team) => ({ value: team.id, label: teamLabel(team) }))
    .filter((option) => option.label !== '')
